import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Users, TrendingUp, DollarSign, Calendar, Phone, Mail, MapPin, Eye, EyeOff, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReservations = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Créer les credentials encodés en base64
      const credentials = btoa(`${username}:${password}`);
      
      console.log('Tentative de connexion avec:', username);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/reservations`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Données reçues:', data.length, 'réservations');
        setReservations(data);
        setIsAuthenticated(true);
        
        // Charger les stats
        const statsResponse = await fetch(`${BACKEND_URL}/api/admin/stats`, {
          method: 'GET',
          headers: {
            'Authorization': `Basic ${credentials}`,
            'Content-Type': 'application/json'
          }
        });
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData);
        }
      } else {
        console.error('Erreur de connexion:', response.status);
        setError(`Nom d'utilisateur ou mot de passe incorrect (${response.status})`);
      }
    } catch (err) {
      console.error('Exception lors de la connexion:', err);
      setError('Erreur de connexion au serveur. Vérifiez la console.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setReservations([]);
    setStats(null);
  };

  const handleClearAllReservations = async () => {
    // Double confirmation pour éviter les suppressions accidentelles
    const firstConfirm = window.confirm(
      `⚠️ ATTENTION!\n\nVous êtes sur le point de SUPPRIMER TOUTES les réservations (${reservations.length} réservations).\n\nCette action est IRRÉVERSIBLE!\n\nÊtes-vous absolument sûr de vouloir continuer?`
    );
    
    if (!firstConfirm) return;
    
    const secondConfirm = window.confirm(
      `🔴 DERNIÈRE CONFIRMATION\n\nToutes les ${reservations.length} réservations seront DÉFINITIVEMENT supprimées.\n\nTapez OK pour confirmer la suppression.`
    );
    
    if (!secondConfirm) return;
    
    setDeleting(true);
    
    try {
      const credentials = btoa(`${username}:${password}`);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/reservations/clear-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ Succès!\n\n${result.deleted_count} réservations supprimées avec succès.`);
        
        // Recharger les données
        setReservations([]);
        
        // Recharger les stats
        const statsResponse = await fetch(`${BACKEND_URL}/api/admin/stats`, {
          method: 'GET',
          headers: {
            'Authorization': `Basic ${credentials}`,
            'Content-Type': 'application/json'
          }
        });
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData);
        }
      } else {
        alert('❌ Erreur lors de la suppression des réservations.');
      }
    } catch (err) {
      console.error('Exception:', err);
      alert('❌ Erreur de connexion au serveur.');
    } finally {
      setDeleting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              🔒 Administration SmartLogis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Nom d'utilisateur</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="smartlogis_admin"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
            <p className="text-gray-600">Gestion des réservations SmartLogis</p>
          </div>
          <Button onClick={handleLogout} variant="outline">
            Déconnexion
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Réservations</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.total_reservations}</p>
                  </div>
                  <Users className="w-12 h-12 text-blue-600 opacity-20" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Revenu Total</p>
                    <p className="text-3xl font-bold text-green-600">
                      {stats.services.reduce((sum, s) => sum + s.revenue, 0).toFixed(2)}$
                    </p>
                  </div>
                  <DollarSign className="w-12 h-12 text-green-600 opacity-20" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Services Actifs</p>
                    <p className="text-3xl font-bold text-purple-600">{stats.services.length}</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-purple-600 opacity-20" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reservations Table */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Liste des Réservations ({reservations.length})</CardTitle>
              {reservations.length > 0 && (
                <Button 
                  onClick={handleClearAllReservations}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? 'Suppression...' : 'Effacer tous les tests'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reservations.map((res, index) => (
                <div key={res.confirmation_number} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Client Info */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">CLIENT</p>
                      <p className="font-semibold text-gray-900">{res.name}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Mail className="w-4 h-4" />
                        <span>{res.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Phone className="w-4 h-4" />
                        <span>{res.phone}</span>
                      </div>
                      {res.address && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate">{res.address}</span>
                        </div>
                      )}
                    </div>

                    {/* Service Info */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">SERVICE</p>
                      <p className="font-semibold text-blue-600">{res.service_name}</p>
                      <p className="text-lg font-bold text-green-600">{res.service_price}$</p>
                      <p className="text-xs text-gray-500 mt-1">N° {res.confirmation_number}</p>
                    </div>

                    {/* Appointment Info */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">RENDEZ-VOUS</p>
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <Calendar className="w-4 h-4" />
                        <span>{res.date} à {res.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <MapPin className="w-4 h-4" />
                        <span>{res.area}</span>
                      </div>
                      <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        {res.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminReservations;
